import React, { useMemo, useState } from 'react';
import { CircleCheck, Info, Warning } from '@youlify/icons';
import classNames from 'classnames';

import Checkbox from '../../checkbox';
import { useWarningBannerStyle } from '../style/warning-banner-trigger';
import { WarningBannerProps } from '../types';

const iconMap = {
  success: CircleCheck,
  warning: Warning,
  info: Info,
  error: Info,
  secondaryInfo: Info,
  warn_pink: Info,
} as const;

const WarningBanner = (props: WarningBannerProps): React.ReactNode => {
  const {
    type = 'info',
    showIcon = true,
    title,
    description,
    list,
    items,
    rows,
    action,
    actionClassName,
    actionStyle,
    checkable,
    checked,
    defaultChecked,
    onChange,
    disabled,
    className,
    style,
  } = props;

  const prefixCls = 'youlify-warning-banner';
  const [wrapSSR, hashId] = useWarningBannerStyle('youlify-warning-banner');

  const isControlled = 'checked' in props;
  const [innerChecked, setInnerChecked] = useState(defaultChecked ?? false);
  const mergedChecked = isControlled ? checked : innerChecked;

  const displayType = useMemo(() => {
    if (checkable && mergedChecked) {
      return 'success';
    }
    return type;
  }, [checkable, mergedChecked, type]);

  const handleChange = (e: { target: { checked: boolean } }) => {
    const next = e.target.checked;
    if (!isControlled) {
      setInnerChecked(next);
    }
    onChange?.(next);
  };

  const Icon = useMemo(() => iconMap[displayType] ?? Info, [displayType]);
  const cls = classNames(
    prefixCls,
    `${prefixCls}-${displayType}`,
    {
      [`${prefixCls}-without-icon`]: !showIcon,
    },
    hashId,
    className,
  );
  const hasItems = Array.isArray(items) && items.length > 0;
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const hasBaseContent = Boolean(title || description || (list && list.length > 0));

  const renderItem = (
    item: NonNullable<WarningBannerProps['items']>[number],
    key: React.Key,
  ): React.ReactNode => {
    const list = item.list ?? [];
    const hasList = list.length > 0;

    return (
      <div className={`${prefixCls}-content-item`} key={key}>
        {item.title && <span className={`${prefixCls}-title`}>{item.title}</span>}
        {item.description && <span className={`${prefixCls}-desc`}>{item.description}</span>}
        {hasList ? (
          <ul className={`${prefixCls}-list`}>
            {list.map((listItem, index) => (
              <li className={`${prefixCls}-list-item`} key={index}>
                {listItem}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  return wrapSSR(
    <div className={cls} style={style}>
      {/* Icon */}
      {showIcon ? (
        <div className={`${prefixCls}-icon`}>
          <Icon />
        </div>
      ) : null}

      {/* Content */}
      <div className={`${prefixCls}-content`}>
        {hasRows ? (
          <div className={`${prefixCls}-content-rows`}>
            {rows.map((row, rowIndex) => (
              <div
                className={`${prefixCls}-content-row`}
                key={rowIndex}
                style={{ gap: row.gap ?? 0 }}
              >
                {row.items.map((item, itemIndex) => renderItem(item, `${rowIndex}-${itemIndex}`))}
              </div>
            ))}
          </div>
        ) : hasItems ? (
          <div className={`${prefixCls}-content-items`}>
            {items.map((item, index) => renderItem(item, index))}
          </div>
        ) : null}
        {hasBaseContent ? renderItem({ title, description, list }, 'base') : null}
      </div>

      {/* Action */}
      {action ? (
        <div className={classNames(`${prefixCls}-extra`, actionClassName)} style={actionStyle}>
          {action}
        </div>
      ) : null}

      {/* Checkbox */}
      {!action && checkable && (
        <div className={`${prefixCls}-extra`}>
          <Checkbox
            className={`${prefixCls}-checkbox`}
            checked={mergedChecked}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>,
  );
};

export default WarningBanner;
